import { useState, useEffect } from 'react';
import { executePrintShippingLabel } from '@src/utils/printReturnLabelUtil';
import {
  IPrintLabelError,
  PrintLabelCallbacks,
  PrintLabelParams,
  SummaryShipment,
} from '@src/models/returns';
import { useSummaryStore } from '@src/stores/summaryStore';
import { useReturnStore } from '@src/stores/returnStore';
import { getWindow } from '@src/utils/general';

interface UsePrintReturnLabelResult {
  isLoading: boolean;
  apiError?: IPrintLabelError;
  retryAvailable: boolean;
  onPrintClick: (isNativeApp?: boolean) => void;
}

export const usePrintReturnLabel = (
  shipment?: SummaryShipment,
): UsePrintReturnLabelResult => {
  const isNativeApp =
    getWindow()?.__EXPERIENCE_CONTEXT__?.isConsumerApp || false;
  const { orderId, emailId, summaryShipments } = useSummaryStore(
    (state) => state,
  );

  // Use provided shipment or fall back to first shipment in summaryShipments
  const targetShipment = shipment ?? summaryShipments?.[0];
  const { trackingDetails, returnTrackingNumbers = [] } = targetShipment || {};

  const orderDetailsV2 = useReturnStore((state) => state.orderDetailsV2);
  const militaryExchange = useReturnStore((state) => state.militaryExchange);
  const isMilitaryExchangeGuest = useReturnStore(
    (state) => state.isMilitaryExchangeGuest,
  );

  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setAPIError] = useState<IPrintLabelError | undefined>();
  const [retryAvailable, setRetryAvailable] = useState(true);

  useEffect(() => {
    if (apiError?.retryable) {
      const timeoutId = setTimeout(() => {
        setRetryAvailable(true);
      }, 1000);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [apiError?.retryable, retryAvailable]);

  const onPrintClick = () => {
    const params: PrintLabelParams = {
      trackingDetails,
      orderId,
      emailId,
      isMilitaryExchangeGuest,
      orderDetailsV2,
      returnTrackingNumbers,
    };

    const callbacks: PrintLabelCallbacks = {
      setIsLoading,
      setAPIError,
      setRetryAvailable,
    };

    executePrintShippingLabel(params, isNativeApp, militaryExchange, callbacks);
  };

  return { isLoading, apiError, retryAvailable, onPrintClick };
};

export default usePrintReturnLabel;
